import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuickNav from "@/pages/home/components/CategoryQuickNav";
import NewsSidebar from "@/pages/home/components/NewsSidebar";
import AboutStrip from "@/pages/home/components/AboutStrip";
import MovieCard from "@/components/base/MovieCard";
import SkeletonCard from "@/components/base/SkeletonCard";
import { movies } from "@/mocks/movies";
import { sortMovies } from "@/utils/movieUtils";

export default function Home() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadData = () => {
    setLoading(true);
    setError(false);
    window.setTimeout(() => {
      if (movies.length === 0) {
        setError(true);
      }
      setLoading(false);
    }, 550);
  };

  useEffect(() => {
    loadData();
  }, []);

  const featuredMovies = movies.filter((movie) => movie.featured);
  const spotlight = featuredMovies[0] ?? movies[0];
  const latestMovies = sortMovies(movies, "latest").slice(0, 6);

  return (
    <div className="flex w-full flex-col pb-4">
      <HeroCarousel movies={featuredMovies.length > 0 ? featuredMovies : movies} />

      <CategoryQuickNav />

      <section className="w-full px-4 pt-14 md:px-6 md:pt-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="flex flex-col gap-10 lg:col-span-2">
            {spotlight && (
              <div>
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">焦点专题</h2>
                  <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
                    编辑精选
                  </span>
                </div>
                <article className="mt-5 overflow-hidden rounded-lg border border-background-300/70 bg-background-50">
                  <div className="relative h-60 w-full md:h-72">
                    <img
                      src={spotlight.still}
                      alt={`${spotlight.title} 剧照`}
                      title={`${spotlight.title} 香港三级片电影`}
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                      <h3 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">{spotlight.title}</h3>
                      <p className="mt-1 text-xs text-background-200/90 md:text-sm">
                        {spotlight.year} · {spotlight.director} 导演 · 主演 {spotlight.cast.slice(0, 2).join("、")}
                      </p>
                    </div>
                  </div>
                  <div className="p-5 md:p-6">
                    <p className="text-sm leading-relaxed text-foreground-600">{spotlight.plot}</p>
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      <Link
                        to={`/detail/${spotlight.id}`}
                        className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                      >
                        阅读完整档案
                        <i className="ri-article-line" />
                      </Link>
                      <span className="flex items-center gap-1 text-sm text-foreground-500">
                        <i className="ri-star-fill text-accent-500" />
                        {spotlight.rating.toFixed(1)} 分
                      </span>
                    </div>
                  </div>
                </article>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">最新收录</h2>
                <Link
                  to="/category/1990s"
                  className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                >
                  全部影片
                  <i className="ri-arrow-right-s-line text-lg" />
                </Link>
              </div>

              {error ? (
                <div className="mt-5 flex flex-col items-center gap-3 rounded-lg border border-background-300/70 bg-background-50 p-10 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-error-warning-line text-2xl" />
                  </span>
                  <p className="text-sm text-foreground-600">内容加载失败，请检查网络后重试</p>
                  <button
                    type="button"
                    onClick={loadData}
                    className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    重新加载
                  </button>
                </div>
              ) : loading ? (
                <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <SkeletonCard key={index} />
                  ))}
                </div>
              ) : (
                <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {latestMovies.map((movie, index) => (
                    <MovieCard key={movie.id} movie={movie} index={index} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <NewsSidebar />
        </div>
      </section>

      <AboutStrip />
    </div>
  );
}